import { IMPORT_STAGE_VALUES, type ImportOrderRow, type ImportStage } from '@palaccount/contracts';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import {
  BulkExportButton,
  DocumentListPage,
  LifecycleStepper,
  normalisePaged,
  ProgressLine,
  type DocumentListConfig,
} from '@/components/document-list';
import { StatusChip, type DocTone } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useBaseCurrency } from '@/lib/session';

import { fetchImportOrdersPage, IMPORTS_SEARCHABLE, useImportOrder, useImportOrders, useImportStats } from './imports-api';
import { importSteps } from './purchase-steppers';
import { PurchasesTabs } from './purchases-tabs';

/**
 * PUR-06 §4's stage pill through the one palette: Draft grey; Confirmed and
 * Shipped blue (moving); In transit and At customs amber (waiting on the
 * carrier, on the broker); Received green; Closed green-faded.
 */
const TONES: Readonly<Record<ImportStage, DocTone>> = {
  draft: 'draft',
  confirmed: 'posted',
  shipped: 'posted',
  in_transit: 'overdue',
  customs_clearance: 'overdue',
  received: 'completed',
  closed: 'closed',
  // Wave 5E.
  cancelled: 'cancelled',
};

type ImportStats = ReturnType<typeof useImportStats>['data'] & object;

const trimQty = (qty: string) => qty.replace(/\.?0+$/, '');

/**
 * PUR-06 §4 on the shared list-and-detail page (wave 3B) — the shipments in
 * flight. The list row is the contract's minimal row (the landed total in
 * base, not the supplier's invoice: landed cost is the whole point of the
 * screen); the selected shipment is read in full through `GET /api/imports/:id`
 * and drawn by `ShipmentDetail` — its seven-stage rail (`import-stages.ts`),
 * the receiving count once goods are on the shelf, its lines at landed cost.
 * The stage select is the list's status filter (GEN-67: every stage).
 */
export function ImportsListPage() {
  const { t } = useTranslation();
  const baseCurrency = useBaseCurrency();

  const stageLabel = (stage: ImportStage) => t(`imports.stage_${stage}`);
  const chip = (row: Pick<ImportOrderRow, 'stage'>) => (
    <StatusChip status={row.stage} tones={TONES} ns="imports" labelKey={`imports.stage_${row.stage}`} />
  );
  const exportColumns = [
    { header: t('imports.colRef'), value: (o: ImportOrderRow) => o.ref },
    { header: t('imports.colSupplier'), value: (o: ImportOrderRow) => o.supplierName },
    { header: t('imports.colStage'), value: (o: ImportOrderRow) => stageLabel(o.stage) },
    { header: t('imports.colArrives'), value: (o: ImportOrderRow) => o.arriveDate, kind: 'date' as const },
    { header: t('imports.colLanded'), value: (o: ImportOrderRow) => o.totalLandedBase, kind: 'number' as const },
  ];
  const toFilters = (filters: { status: ImportStage | ''; dateFrom: string; dateTo: string }) => ({
    stage: filters.status || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
  });

  const config: DocumentListConfig<ImportOrderRow, ImportStage, ImportStats> = {
    titleKey: 'imports.title',
    tabs: <PurchasesTabs />,
    newAction: { to: '/purchases/imports/new', labelKey: 'common.new', permission: 'purchasing.create', reasonKey: 'perm.cannot.createImports' },
    // Wave 5E — the filter offers every stage a shipment can be in, cancelled included.
    statuses: IMPORT_STAGE_VALUES,
    statusLabel: stageLabel,
    search: IMPORTS_SEARCHABLE ? { labelKey: 'doclist.searchLabel', placeholderKey: 'doclist.searchPlaceholder' } : undefined,
    dateRange: true,
    useList: (filters, page, pageSize) => {
      const query = useImportOrders(toFilters(filters), page, pageSize);
      return { data: normalisePaged(query.data, 'orders'), isPending: query.isPending, isError: query.isError };
    },
    /* The deep link reads the full shipment and folds it to the list's row shape. */
    useById: (id) => {
      const query = useImportOrder(id ?? undefined);
      const order = query.data;
      return {
        data:
          order === undefined
            ? undefined
            : {
                id: order.id,
                ref: order.ref,
                stage: order.stage,
                supplierName: order.supplierName,
                arriveDate: order.arriveDate,
                totalLandedBase: order.summary.totalLandedBase,
              },
      };
    },
    kpis: {
      useStats: () => {
        const stats = useImportStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      tiles: [
        { key: 'transit', labelKey: 'imports.tileInTransit', format: 'count', value: (s) => s.inTransit },
        { key: 'arriving', labelKey: 'imports.tileArriving', format: 'count', value: (s) => s.arriving14d, tone: 'warn' },
        { key: 'value', labelKey: 'imports.tileValueInTransit', format: 'moneyWhole', value: (s) => s.valueInTransit, currency: (s) => s.currency },
        { key: 'unallocated', labelKey: 'imports.tileLandedUnallocated', format: 'count', value: (s) => s.landedNotAllocated, tone: 'warn' },
      ],
    },
    card: (row) => ({
      code: row.ref,
      chip: chip(row),
      title: row.supplierName,
      meta: (
        <>
          {t('imports.colArrives')} <bdi dir="ltr">{row.arriveDate}</bdi>
        </>
      ),
      amount: { value: row.totalLandedBase, currency: baseCurrency },
    }),
    columns: { codeKey: 'imports.colRef', titleKey: 'imports.colSupplier', metaKey: 'imports.colArrives', amountKey: 'imports.colLanded', statusKey: 'imports.colStage' },
    emptyKey: 'imports.empty',
    selectOneKey: 'imports.selectOne',
    export: {
      filename: 'import-orders',
      columns: exportColumns,
      fetchPage: (filters, page, pageSize) => fetchImportOrdersPage(toFilters(filters), page, pageSize),
    },
    bulkActions: (rows) => <BulkExportButton filename="import-orders" columns={exportColumns} rows={rows} />,
    detail: {
      chip,
      render: (row) => <ShipmentDetail id={row.id} />,
    },
  };

  return <DocumentListPage config={config} />;
}

/** The selected shipment, read in full: the stage rail, the receiving count, the lines at landed cost. */
function ShipmentDetail({ id }: { id: string }) {
  const { t } = useTranslation();
  const baseCurrency = useBaseCurrency();
  const { data: order, isPending, isError } = useImportOrder(id);
  if (isPending) return <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />;
  if (isError || order === undefined) {
    return (
      <p role="alert" className="py-8 text-center text-sm text-destructive">
        {t('common.loadFailed')}
      </p>
    );
  }
  return (
    <>
      <p className="text-sm text-muted-foreground">
        {order.supplierName} ·{' '}
        {order.purchaseOrderCode === null ? (
          t('imports.directImport')
        ) : (
          <Ltr value={order.purchaseOrderCode} className="font-mono" />
        )}{' '}
        · {order.warehouseName}
      </p>
      <LifecycleStepper steps={importSteps(order)} label={t('imports.stepper')} />
      {order.receiving.open ? (
        <ProgressLine
          text={t('imports.listProgress', {
            received: trimQty(order.receiving.receivedQty),
            ordered: trimQty(order.receiving.orderedQty),
          })}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline">
          <Link to={`/purchases/imports/${order.id}`}>
            <Pencil aria-hidden /> {t('doclist.open')}
          </Link>
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('quotes.colItem')}</TableHead>
            <TableHead className="w-28 text-end">{t('sales.colQty')}</TableHead>
            <TableHead className="w-32 text-end">{t('imports.colLandedUnit')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {order.lines.map((line) => (
            <TableRow key={line.id}>
              <TableCell className="font-medium">{line.name}</TableCell>
              <TableCell className="text-end">
                <Qty value={line.qty} unit={line.unitSymbol} precision={line.unitPrecision} />
              </TableCell>
              <TableCell className="text-end">
                <Amount value={line.landedUnitCostBase} currency={baseCurrency} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-end text-sm">
        <span className="text-muted-foreground">{t('imports.colLanded')}</span>{' '}
        <Amount value={order.summary.totalLandedBase} currency={baseCurrency} className="font-bold" />
      </p>
    </>
  );
}
